import { Button } from "@blueprintjs/core";
import classNames from "classnames";
import { selectAll } from "d3";
import React, { MouseEventHandler, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Scrollbar } from "react-scrollbars-custom";

import { EditPlay, LoadingPage, PlayDescription, TIMELINE_VIEW, useEditPlay } from "../../components";
import { AnimationTimeline } from "../../components/animation-timeline/animation-timeline";
import { DrawToolbar } from "../../components/draw-toolbar/draw-toolbar";
import { PlayingSurface } from "../../components/playing-surface/playing-surface";
import { useGetSinglePlay } from "../../redux/slices";
import { PlayRouteParams, SportPlay } from "../../types";
import { getOutofBounds } from "../../utils";
import { PlayTabHeader } from "./components";
import { ToolPane } from "./components/tool-pane/tool-pane";
import { usePlayDataKeyboardListener, useResizeLine } from "./hooks";
import { usePlayContextApi } from "./hooks/use-play-context-api";
import { PlayContext } from "./play-context";
import styles from "./play.tab.module.scss";

export interface PlayProps<TPlay> {
  play: TPlay;
}

export const Play = () => {
  const { playId } = useParams<PlayRouteParams>();
  const { data: play } = useGetSinglePlay(playId);

  if (!play) return <LoadingPage />;

  return <PlayTab play={play} />;
};

const PlayTab = <TPlay extends SportPlay>({ play }: PlayProps<TPlay>) => {
  const location = useLocation();
  /** ======== Play Context API ======== */
  const playContextApi = usePlayContextApi(play);
  const { selectItemApi, playStateApi, animationTimerApi, drawToolbarApi } = playContextApi;

  const { clearItems } = selectItemApi;
  const {
    playState,
    activeFrame,
    activeFrameStencils,
    updateActiveFrame,
    setActiveFrameId,
    updatePartialPlay,
    setPlayState,
  } = playStateApi;
  const { timelineView, effectiveTime, setTime, pauseTimer, resetTimer, frameToStartTimeScale } = animationTimerApi;
  const { animationView } = drawToolbarApi;

  /** ======== Edit Play ======== */
  const onEditPlay = (play: TPlay) => {
    setPlayState(play);
  };
  const editPlayProps = useEditPlay({ onSave: onEditPlay });

  const { showEditPlay } = editPlayProps;
  const handleShowEditPlay = () => {
    showEditPlay(play);
  };

  // on click the court container.
  const handleClickCourtContainer: MouseEventHandler<HTMLDivElement> = (event) => {
    const { target } = event;
    // get all stencil wrapper nodes
    const d3StencilWrapper = selectAll<SVGGElement, unknown>(".stencil-wrapper");
    const stencilWrapperNodes = d3StencilWrapper.nodes();
    // check if clicked element is within a stencil, and if not clear items
    if (!stencilWrapperNodes.some((node) => node.contains(target as SVGElement))) clearItems();
  };

  const isTime0 = effectiveTime === 0;

  const resizeLineRef = useRef<HTMLDivElement>(null);
  const { animationWindowHeight } = useResizeLine({ ref: resizeLineRef });

  /** Global Keyboard Listeners */
  usePlayDataKeyboardListener({ playContextApi });

  const isDevEnvironment = window.location.host.includes("localhost:3000");

  const logFrameStencils = () => {
    console.log(activeFrameStencils);
  };

  return (
    <div className={styles.playTab} onClick={handleClickCourtContainer}>
      <PlayContext.Provider value={playContextApi}>
        <PlayTabHeader play={playState} showEditPlayDetailsModal={handleShowEditPlay} />
        <div className={styles.playTabContent}>
          <ToolPane />
          {isDevEnvironment && (
            <Button className={styles.devLogButton} onClick={logFrameStencils}>
              Log Frame Stencils
            </Button>
          )}
          <div className={styles.centerContent}>
            <div
              className={classNames(styles.centerContentDisplay, {
                [styles?.animationTimelineOpen || ""]: animationView.isActive,
              })}
              style={{ height: `calc(100% - ${animationView.isActive ? animationWindowHeight : 0}px - 58px)` }}
            >
              <div className={styles.court}>
                <PlayingSurface
                  play={playState}
                  stencils={activeFrameStencils}
                  outOfBounds={getOutofBounds(playState)}
                  isEditable
                />
                {!isTime0 && (
                  <div
                    className={styles.timeResetOverlay}
                    onClick={() => {
                      pauseTimer();
                      resetTimer();
                    }}
                  />
                )}
              </div>
              {activeFrame && (
                <PlayDescription
                  updateActiveFrame={updateActiveFrame}
                  frame={activeFrame}
                  play={playState}
                  updatePartialPlay={updatePartialPlay}
                />
              )}
            </div>
            {activeFrame && <DrawToolbar {...drawToolbarApi} />}
            <div
              className={classNames(styles.resizeLine, { [styles.active || ""]: animationView.isActive })}
              ref={resizeLineRef}
            />
            {animationView.isActive && activeFrame && <AnimationTimeline height={animationWindowHeight} />}
          </div>
          <div className={styles.frameList}>
            <Scrollbar permanentTrackY>
              {playState.frames.map((frame, i) => (
                <div
                  className={classNames(styles.frame, { [styles?.activeFrame || ""]: activeFrame?.id === frame?.id })}
                  onClick={() => {
                    if (timelineView === TIMELINE_VIEW.FRAME) setActiveFrameId(frame.id);
                    else setTime(frameToStartTimeScale(frame.id));
                  }}
                  key={i}
                >
                  <div className={styles.frameLabel}>{frame.label || `Frame ${i + 1}`}</div>
                  <div className={styles.playingSurfaceContainer}>
                    <PlayingSurface
                      play={playState}
                      stencils={frame.frameStencils || []}
                      outOfBounds={getOutofBounds(playState)}
                    />
                  </div>
                </div>
              ))}
            </Scrollbar>
          </div>

          <EditPlay {...editPlayProps} />
        </div>
      </PlayContext.Provider>
    </div>
  );
};
